import { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/ui/dialog";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { IconPlus } from "@tabler/icons-react";

interface Placeholder {
  name: string;
  description: string;
  type: string;
}

interface AddItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupName: string;
  placeholders: Placeholder[];
  elementType?: string;
  onAdd: (values: Record<string, string>) => void;
}

// Match placeholder names flexibly
function isTextPlaceholder(name: string): boolean {
  const upper = name.toUpperCase();
  return upper.includes("TEXT") || upper.includes("LABEL") || upper.includes("CONTENT");
}

function isHrefPlaceholder(name: string): boolean {
  const upper = name.toUpperCase();
  return upper.includes("HREF") || upper.includes("URL") || upper.includes("LINK");
}

// Generate friendly display name from placeholder name
function getFriendlyName(name: string): string {
  const upper = name.toUpperCase();
  // Common semantic names
  if (upper === "TITLE" || upper.includes("TITLE")) return "Title";
  if (upper === "DESCRIPTION" || upper.includes("DESCRIPTION") || upper.includes("DESC")) return "Description";
  if (upper === "SUBTITLE" || upper.includes("SUBTITLE")) return "Subtitle";
  if (upper === "PRICE" || upper.includes("PRICE")) return "Price";
  if (upper === "DATE" || upper.includes("DATE")) return "Date";
  if (upper === "AUTHOR" || upper.includes("AUTHOR")) return "Author";
  if (upper === "NAME" || upper.includes("NAME")) return "Name";
  if (upper === "ROLE" || upper.includes("ROLE")) return "Role";
  if (upper === "BADGE" || upper.includes("BADGE")) return "Badge";
  if (upper === "LABEL" || upper.includes("LABEL")) return "Label";
  if (isHrefPlaceholder(name)) return "Url";
  if (isTextPlaceholder(name)) return "Text";
  // Fallback: capitalize the name
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

export function AddItemDialog({
  open,
  onOpenChange,
  groupName,
  placeholders,
  elementType,
  onAdd,
}: AddItemDialogProps) {
  const [values, setValues] = useState<Record<string, string>>({});

  // Reset values when dialog opens
  useEffect(() => {
    if (open) {
      setValues({});
    }
  }, [open]);

  // Show ALL placeholders with friendly names
  // For compound elements (cards with title+description), show all fields
  const displayPlaceholders = useMemo(() => {
    const isLink = elementType === "link";

    // If we have placeholders, show them all
    if (placeholders && placeholders.length > 0) {
      // Map all placeholders with friendly names
      const result = placeholders.map(p => ({
        ...p,
        friendlyName: getFriendlyName(p.name),
      }));

      // For links, ensure we have both text and href
      if (isLink) {
        const hasHref = placeholders.some(p => isHrefPlaceholder(p.name));
        // Check for any text-like placeholder (NAME, TITLE, LABEL, TEXT, etc.)
        const hasText = placeholders.some(p => {
          const upper = p.name.toUpperCase();
          return isTextPlaceholder(p.name) ||
            upper === "TITLE" ||
            upper === "LABEL" ||
            upper === "NAME" ||
            upper === "DESCRIPTION" ||
            p.type === "text";  // Also check the type field
        });

        if (!hasHref) {
          result.push({ name: "HREF", description: "Link URL", type: "href", friendlyName: "Url" });
        }
        if (!hasText) {
          result.unshift({ name: "TEXT", description: "Text content", type: "text", friendlyName: "Label" });
        }
      }

      return result;
    }

    // Fallback: create default placeholders
    const result: Array<Placeholder & { friendlyName: string }> = [
      { name: "TEXT", description: "Text content", type: "text", friendlyName: "Label" },
    ];

    if (isLink) {
      result.push({ name: "HREF", description: "Link URL", type: "href", friendlyName: "Url" });
    }

    return result;
  }, [placeholders, elementType]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(values);
    setValues({});
  };

  const handleChange = (name: string, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }));
  };

  const isValid = displayPlaceholders.every(
    p => values[p.name] && values[p.name].trim() !== ""
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-base">Add to {groupName}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          {displayPlaceholders.map(placeholder => (
            <div key={placeholder.name} className="space-y-1.5">
              <Label htmlFor={placeholder.name} className="text-xs font-medium">
                {placeholder.friendlyName}
              </Label>
              <Input
                id={placeholder.name}
                value={values[placeholder.name] || ""}
                onChange={e => handleChange(placeholder.name, e.target.value)}
                placeholder={isHrefPlaceholder(placeholder.name) ? "https://..." : "Enter text"}
                className="h-8 text-sm"
              />
            </div>
          ))}


          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!isValid}
            >
              <IconPlus className="w-3.5 h-3.5 mr-1" />
              Add
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
